import { Delete20Regular, Info20Regular } from '@fluentui/react-icons';
import { Flex } from '../../../../Flex';
import { Tooltip } from '../../../../Tooltip';
import { CourseTeacher } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import moment from 'moment';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarUser } from '../../../../Avatar';
import { ListEntryProps } from '../../../../List';
import { PersonList, PersonListProps } from '../../../../PersonList/PersonList';
import './CourseTeacher.css';

export type CourseTeacherListProps = Omit<
  PersonListProps<CourseTeacher>,
  'getPersonContent'
>;

export const CourseTeacherList: React.FC<CourseTeacherListProps> = props => {
  const { t } = useTranslation();

  const getTeacherListContent = (person: CourseTeacher): ReactNode => {
    const items: ReactNode[] = [];

    if (person.isSubstitution || person.removalDate) {
      items.push(
        <React.Fragment key="substitution">
          {person.isSubstitution && (
            <div className="substitutionTag">
              {t('classbook:courseTeacherList:substitutionTagLabel', { defaultValue: 'Vertretung' })}
            </div>
          )}
          {person.removalDate && (
            <span>
              {`${t('classbook:courseTeacherList:removalDateLabel', { defaultValue: 'Eingetragen bis' })}: ${moment(person.removalDate).format('ll')}`}
            </span>
          )}
        </React.Fragment>,
      );
    }

    items.push(<span key="mail">{person.mailAddress}</span>);

    if (person.isExternal) {
      items.push(
        <span key="external">
          {t('classbook:externally_managed', { defaultValue: 'Extern verwaltet' })}
        </span>,
      );
    }

    return (
      <Flex align="center" gap="0.25rem">
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span>·</span>}
            {item}
          </React.Fragment>
        ))}
      </Flex>
    );
  };

  const getPersonListEntry = (
    courseTeacher: CourseTeacher,
  ): ListEntryProps => ({
    label: courseTeacher.displayName,
    avatar: (
      <LazyAvatarUser
        name={courseTeacher.displayName}
        userId={courseTeacher.id}
      />
    ),
    content: getTeacherListContent(courseTeacher),
    disabled: courseTeacher.isExternal,
    actions: [
      courseTeacher.isExternal
        ? {
            icon: (
              <Tooltip
                trigger={<Info20Regular aria-label={t('classbook:externally_managed_hint', { defaultValue: 'Die Zuordnung dieses Benutzers stammt aus externer Quelle. Manuelle Änderungen solcher Zuordnungen sind nicht möglich.' })} />}
                content={t('classbook:externally_managed_hint', { defaultValue: 'Die Zuordnung dieses Benutzers stammt aus externer Quelle. Manuelle Änderungen solcher Zuordnungen sind nicht möglich.' })}
              />
            ),
          }
        : {
            icon: (
              <Delete20Regular
                aria-label={t('classbook:course:teacherlist:deleteteacher_label', { defaultValue: 'Benutzer wird aus der Zuordnung gelöscht.' })}
              />
            ),
            disabled: courseTeacher.isSubstitution,
            'data-testid': 'removePersonButton',
            onClick: () =>
              props.onUpdate(
                props.selectedPersons.filter(
                  selected => selected.id !== courseTeacher.id,
                ),
              ),
          },
    ],
  });

  return (
    <PersonList
      {...props}
      getPersonListEntry={getPersonListEntry}
      className="courseTeacherList"
    />
  );
};
